import { trace } from "@nopeer";
import { Loading } from "../ux/Loading.js";

export class Fetch {
	static BASE_URL = window.__API_BASE_URL__ || "";

	static #request = (method, url, data = {}, headers = {}, showLoading = true) => {
		const finalUrl = url.startsWith('http') ? url : `${this.BASE_URL}${url}`;

		if (showLoading) {
			Loading.show();
		}

		// 1. 기본 헤더 설정
		//const headers = {};
		//Object.assign(headers, customHeaders);
		const finalHeaders = { ...headers };

		// 🎯 2. LocalStorage에서 JWT 토큰이 있으면 자동 장착 (사용자가 별도로 Authorization을 안 넘겼을 때)
		const token = localStorage.getItem("accessToken");
		if (token && !finalHeaders["Authorization"] && !finalHeaders["authorization"]) {
			finalHeaders["Authorization"] = `Bearer ${token}`;
		}

		// 🔴 핵심: 데이터가 FormData가 아닐 때만 JSON 헤더를 추가합니다.
		if (!(data instanceof FormData)) {
			finalHeaders["Content-Type"] = "application/json";
		}

		const options = {
			method,
			headers: finalHeaders
		};

		let targetUrl = finalUrl;
		if (method === "GET") {
			targetUrl += `?${new URLSearchParams(data)}`;
		} else {
			// 🔴 핵심: FormData는 그대로 body에 넣고, 일반 객체는 JSON.stringify 처리합니다.
			options.body = data instanceof FormData ? data : JSON.stringify(data);
		}

		return fetch(targetUrl, options)
			.then(async res => {
				if (!res.ok) {
					const err = await res.json().catch(() => ({}));
					const errorMessage = err.message || `API 오류 (${res.status})`;

					// 🎯 백엔드에서 BizException 등으로 처리된 400 에러(비즈니스 예외)일 때만 공통 알럿 띄우기
					if (res.status === 400) {
						nine?.alert(errorMessage).rgb().shake();
					} else {
						// 500 에러 등 시스템 장애인 경우 콘솔이나 다른 방식으로 처리
						console.error("시스템 오류:", errorMessage);
					}

					throw new Error(errorMessage);
				}

				/**
				if (!res.ok) {
					const text = await res.text();
					throw new Error(`API 오류 (${res.status}): ${text}`);
				}*/
				return res.json();
			})
			.catch(err => {
				trace.error(`[IdeFetch.${method.toLowerCase()}] ${finalUrl} 실패:`, err);
				throw err;
			})
			.finally(() => {
				if (showLoading) {
					Loading.hide();
				}
			});
	};

	// 기존 post/get 메서드도 인자를 넘겨주도록 수정
	static get = (url, data = {}, header = {}, showLoading = true) => this.#request("GET", url, data, header, showLoading);
	static post = (url, data = {}, header = {}, showLoading = true) => this.#request("POST", url, data, header, showLoading);

	/**header
	 * 🚀 범용 청크 전송 (파라미터 이름까지 동적 설정)
	 * @param {string} url - API 주소
	 * @param {Array} rows - 데이터 배열
	 * @param {object} options - 상세 옵션
	 * - fileKey: 객체 내 파일 속성명 (기본: 'fileContents')
	 * - filePartName: 서버에서 받을 파일 파트명 (기본: 'files')
	 * - jsonPartName: 서버에서 받을 JSON 파트명 (기본: 'dataList')
	 * - chunkSize: 분할 단위 (기본: 10)
	 */
	static postMultipart = async (url, rows = [], options = {}) => {
		const {
			fileKey = 'fileContents',
			filePartName = 'files',
			jsonPartName = 'dataList',
			chunkSize = 10
		} = options;

		const dataRows = Array.isArray(rows) ? rows : [rows];

		Loading.show();
		let totalCount = 0;
		let lastResponse = null;

		return (async () => {

			let globalFileIndex = 0;

			for (let i = 0; i < dataRows.length; i += chunkSize) {
				const formData = new FormData();
				const chunk = dataRows.slice(i, i + chunkSize);
				const rowDataList = [];

				chunk.forEach((row, rowIndex) => {
					// row에서 파일 데이터와 나머지 데이터를 분리
					const { [fileKey]: fileData, ...rest } = row;
					//rowDataList.push(rest);

					// 2번 방식의 핵심: 배열이든 아니든 명확히 순서를 지킴
					const files = Array.isArray(fileData) ? fileData : [fileData];

					const fileIndices = [];
					files.forEach(fileObj => {
						if (fileObj instanceof File) {
							formData.append(filePartName, fileObj);
							fileIndices.push(globalFileIndex++);
						}
					});

					rowDataList.push({ ...rest, fileIndices });
				});

				// JSON 데이터를 FormData에 추가
				//const jsonBlob = new Blob([JSON.stringify(rowDataList)], { type: 'application/json' });
				const jsonBlob = new Blob(
					[JSON.stringify(Array.isArray(rows) ? rowDataList : rowDataList[0])],
					{ type: 'application/json' }
				);
				formData.append(jsonPartName, jsonBlob);

				lastResponse = await this.post(url, formData, false);

				if (lastResponse && (lastResponse.success || lastResponse.status === 'OK')) {
					totalCount += chunk.length;
				} else {
					throw new Error(`청크 전송 실패: ${i}번째 섹션`);
				}
			}
			return { ...lastResponse, success: true, totalCount };
		})()
			.finally(() => Loading.hide());
	};

	static postOneMultipart = async (url, row = {}, options = {}) => {
		const {
			fileKey = 'fileContents',
			jsonPartName = 'dataList'
		} = options;

		Loading.show();

		try {
			const formData = new FormData();
			const { [fileKey]: fileData, ...rest } = row;

			// 🎯 재귀적으로 파일을 찾아 FormData에 추가하는 함수
			const appendFiles = (key, value) => {
				if (Array.isArray(value)) {
					// 배열이면 각 요소를 동일한 키로 append (서버의 List<MultipartFile>과 매칭)
					value.forEach(item => appendFiles(key, item));
				} else if (value instanceof File) {
					formData.append(key, value);
				} else if (value && typeof value === 'object') {
					// 객체면 키를 유지하며 내부 탐색
					Object.entries(value).forEach(([k, v]) => appendFiles(k, v));
				}
			};

			// 1. 파일 데이터 추가
			if (fileData && typeof fileData === 'object') {
				Object.entries(fileData).forEach(([key, value]) => {
					appendFiles(key, value);
				});
			}

			// 2. 나머지 데이터 추가
			const jsonBlob = new Blob([JSON.stringify(rest)], { type: 'application/json' });
			formData.append(jsonPartName, jsonBlob);

			const response = await this.post(url, formData, false);

			if (response && (response.success || response.status === 'OK')) {
				return { ...response, success: true };
			} else {
				throw new Error("전송 실패");
			}
		} catch (err) {
			trace.error("[postOneMultipart] Error:", err);
			throw err;
		} finally {
			Loading.hide();
		}
	};

	static postMultipart_BAK = async (url, rows = [], options = {}) => {
		const {
			fileKey = 'fileContents',
			filePartName = 'files',
			jsonPartName = 'dataList',
			chunkSize = 10
		} = options;

		// 1. 시작 전 로딩바 띄우기
		Loading.show();

		let totalCount = 0;
		let lastResponse = null;

		// 🔴 2. 전체 루프를 Promise 체인처럼 관리 (내부는 await 유지)
		return (async () => {
			for (let i = 0; i < rows.length; i += chunkSize) {
				const formData = new FormData();
				const chunk = rows.slice(i, i + chunkSize);
				const rowDataList = [];

				chunk.forEach((row) => {
					const { [fileKey]: fileObj, _fileObj, ...rest } = row;
					rowDataList.push(rest);
					if (fileObj instanceof File) {
						formData.append(filePartName, fileObj);
					}
				});

				const jsonBlob = new Blob([JSON.stringify(rowDataList)], { type: 'application/json' });
				formData.append(jsonPartName, jsonBlob);

				// showLoading = false로 내부 호출
				lastResponse = await this.post(url, formData, false);

				if (lastResponse && (lastResponse.success || lastResponse.status === 'OK')) {
					totalCount += chunk.length;
				} else {
					throw new Error(`청크 전송 실패: ${i}번째 섹션`);
				}
			}
			return { ...lastResponse, success: true, totalCount };
		})()
			.catch(err => {
				trace.error("[postChunk] Error:", err);
				throw err;
			})
			.finally(() => {
				// 🔴 3. 성공하든 실패하든 여기서 깔끔하게 닫기!
				Loading.hide();
			});
	};
}

export const api = Fetch;